import { useSelector, useDispatch } from "react-redux";
import { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';

//Helpers
import { getBadget } from 'Core/data/Helpers';

//Components
import { Form, Button, Input, Select, Tooltip } from "@gpstrackit/react-controls";
import CustomSelect from 'Components/Select';
import { Grid, Icon } from '@mui/material';

//Actions
import { sizeDashboardPanelCreateEditModal, searchEntityRequest } from 'Redux/actions'
import { getAccounts } from 'Modules/dashboard/views/.components/chart/.components/forms/createEditDeviceByStatus/redux/saga';

const CreateEditDeviceByStatus = (props) => {
    const { messages } = useIntl();
    const { onSubmit, handleClose } = props;

    const dispatch = useDispatch();
    const devicesByStatusForm = useRef();

    //Local States
    const [canceled, setCanceled] = useState(true)
    const [rma, setRma] = useState(false)
    const [voluntarySuspension, setVoluntarySuspension] = useState(false)
    const [formData, setFormData] = useState({});

    //Redux States
    const { loadingCreateEdit, gadGet } = useSelector((state) => state.dashboardPanelMainRedux);
    const { dealers, dealersLoading, manufacturers, manufacturersLoading, deviceModels, deviceModelsLoading } = useSelector(state => state.generalRedux);
    const tags = useSelector(state => state.generalRedux.tags);
    const loadingTags = useSelector(state => state.generalRedux.loadingEntity);

    const optionsFrecuency = [
        { id: 300, name: messages['dashboard_gadget_five_minutes'] },
        { id: 900, name: messages['dashboard_gadget_fifteen_minutes'] },
        { id: 1800, name: messages['dashboard_gadget_thirty_minutes'] },
        { id: 3600, name: messages['dashboard_gadget_one_hour'] },
        { id: 7200, name: messages['dashboard_gadget_two_hour'] }
    ];

    const validateData = () => {
        let unitStatus = []
        if (canceled) unitStatus.push(4)
        if (rma) unitStatus.push(6)
        if (voluntarySuspension) unitStatus.push(2)
        onSubmit({
            ...formData,
            refreshSeconds: formData?.refreshSeconds?.id,
            age: parseInt(formData?.age),
            unitStatus,
            dealerId: formData?.dealer?.id,
            accountId: formData?.account?.id,
            manufacturerId: formData?.manufacturer?.id,
            deviceModelId: formData?.deviceModel?.id,
            type: 'DevicesByStatus',
        })
    }

    const getAccountsForSelect = (input, callback) => {
        let dealerId = formData?.dealer?.id
        if (!input) {
            callback([])
            return
        }
        getAccounts(input, dealerId).then((response) => {
            if (response.result) {
                const { items } = response?.result
                callback(items)
            }
        })
    }

    useEffect(() => {
        if (gadGet.hasOwnProperty('name')) {
            let settings = JSON.parse(gadGet.settings);
            if (settings?.unitStatus) {
                if (settings?.unitStatus.includes(4)) {
                    setCanceled(true)
                } else {
                    setCanceled(false)
                }
                if (settings?.unitStatus.includes(6)) setRma(true)
                if (settings?.unitStatus.includes(2)) setVoluntarySuspension(true)
            }
            setFormData({
                name: settings.name,
                age: settings.age,
                dealer: settings?.dealer,
                account: settings?.account,
                manufacturer: settings?.manufacturer,
                deviceModel: settings?.deviceModel,
                refreshSeconds: optionsFrecuency.filter(frecuency => frecuency.id === gadGet.refreshSeconds)[0],
                tagIds: settings?.tagIds || []
            })
        }
    }, [])

    useEffect(() => {
        dealers?.length === 0 && dispatch(searchEntityRequest({ key: "dealers" }));
        manufacturers?.length === 0 && dispatch(searchEntityRequest({ key: "manufacturers" }));
        deviceModels?.length === 0 && dispatch(searchEntityRequest({ key: "deviceModels" }));
        dispatch(searchEntityRequest({ key: "tags" }));
    }, [])

    return (
        <div style={{ padding: '15px 10px 20px 10px', marginBottom: 100 }}>
            <Form ref={devicesByStatusForm} onValidSubmit={validateData}>
                <div>
                    <Input
                        id="device-status-name"
                        value={formData?.name || ''}
                        onChange={(value) => setFormData({ ...formData, name: value })}
                        minLength={1}
                        maxLength={40}
                        label={messages['dashboard_gadget_title']}
                        placeholder={messages['dashboard_gadget_title']}
                        type="text"
                        required
                        requiredMessage={messages['required_field']}
                    />
                </div>
                <div style={{ marginTop: 10 }}>
                    <Select
                        id="device-status-refresh-seconds"
                        label={messages['dashboard_gadget_frequency_refresh']}
                        placeholder={messages['dashboard_gadget_frequency_refresh']}
                        options={optionsFrecuency}
                        textField="name"
                        valueField="id"
                        isSearchable={false}
                        value={formData.refreshSeconds}
                        onChange={(value) => setFormData({ ...formData, refreshSeconds: value })}
                        isClearable
                        required
                        requiredMessage={messages['required_field']}
                    />
                </div>
                <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex' }}>
                        <label className="input-normal-label">{messages['dashboard_gadget_age']}<span className="text-danger"> *</span></label>
                        <Tooltip title={messages['dashboard_gadget_define_maximum']}>
                            <Icon style={{ cursor: 'pointer', fontSize: 20, marginLeft: 5 }}>help</Icon>
                        </Tooltip>
                    </div>
                    <Input
                        id="device-status-age"
                        value={formData?.age}
                        onChange={(value) => setFormData({ ...formData, age: value })}
                        min={1}
                        max={181}
                        minLength={1}
                        maxLength={3}
                        placeholder={messages['dashboard_gadget_age']}
                        type="number"
                        required
                        requiredMessage={messages['required_field']}
                        maxMessage={`${messages["create_device_model_max_number"]} 180`}
                        minMessage={`${messages["create_device_model_min_number"]} 1`}
                        minLengthMessage={`${messages['dashboard_gadget_min_length_message']}1`}
                        maxLengthMessage={`${messages['dashboard_gadget_max_length_message']}2`}
                    />
                </div>
                <div style={{ marginLeft: 0 }}>
                    <Grid container>
                        <Grid item xs={12} style={{ marginTop: 8 }}>
                            <label className="input-normal-label">{messages['create_edit_gadget_unit_status']}<span className="text-danger"> *</span></label>
                        </Grid>
                        <Grid item xs={12}>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="low-bit-rate-low"
                                onClick={e => {
                                    if (canceled && !rma && !voluntarySuspension) { } else {
                                        setCanceled(!canceled)
                                    }

                                }}
                            >
                                {getBadget(messages['cancelled'], canceled)}
                            </div>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="low-bit-rate-low"
                                onClick={e => {
                                    if (rma && !canceled && !voluntarySuspension) { } else {
                                        setRma(!rma)
                                    }
                                }}
                            >
                                {getBadget("RMA", rma)}
                            </div>
                            <div
                                className="d-sm-inline-block mb-1 vidfleet-pills"
                                key="low-bit-rate-low"
                                onClick={e => {
                                    if (voluntarySuspension && !canceled && !rma) { } else {
                                        setVoluntarySuspension(!voluntarySuspension)
                                    }

                                }}
                            >
                                {getBadget(messages['voluntary_suspension'], voluntarySuspension)}
                            </div>
                        </Grid>
                    </Grid>
                </div>
                <div style={{ marginTop: 10 }}>
                    <Select
                        id="device-status-dealer"
                        label={messages['create_edit_package_brand']}
                        placeholder={messages['create_edit_package_brand']}
                        options={dealers || []}
                        valueField="id"
                        textField="name"
                        value={formData?.dealer}
                        onChange={(value) => setFormData({ ...formData, dealer: value, account: null })}
                        requiredMessage={messages['required_field']}
                        disabled={dealersLoading}
                        isClearable
                    />
                </div>
                <div style={{ marginTop: 10 }}>
                    <CustomSelect
                        async
                        value={formData.account}
                        onChange={(item) => {
                            setFormData({ ...formData, account: item })
                        }}
                        label={messages['devices_import_account']}
                        placeholder={messages["devices_import_account"]}
                        loadOptions={getAccountsForSelect}
                        getOptionLabel={e => e.name}
                        getOptionValue={e => e.id}
                        isClearable
                    />
                </div>
                <div style={{ marginTop: 10 }}>
                    <Select
                        id="device-status-manufacturer"
                        label={messages['devices_main_manufacturer']}
                        placeholder={messages["devices_main_manufacturer"]}
                        value={formData?.manufacturer}
                        onChange={(value) => setFormData({ ...formData, manufacturer: value, deviceModel: null })}
                        options={manufacturers || []}
                        textField="name"
                        valueField="id"
                        menuPlacement="auto"
                        isClearable
                        loading={manufacturersLoading}
                    />
                </div>
                <div style={{ marginTop: 10 }}>
                    <Select
                        id="device-status-device-model"
                        label={messages['devices_main_device_model']}
                        placeholder={messages["devices_main_device_model"]}
                        value={formData?.deviceModel}
                        onChange={(value) => setFormData({ ...formData, deviceModel: value })}
                        options={formData?.manufacturer ? deviceModels && deviceModels.filter(x => x.manufacturerId === +formData?.manufacturer.id) : []}
                        textField="name"
                        valueField="id"
                        menuPlacement="auto"
                        isClearable
                        loading={deviceModelsLoading}
                    />
                </div>
                <Grid item xs={12} style={{ textAlign: 'left', marginTop: 10, marginBottom: 5 }}>
                    <Select
                        id="tags-selector"
                        label={messages['accounts_main_tags']}
                        value={formData?.tagIds?.length ? tags.filter((item) => formData?.tagIds.find(x => x === item.id)) : []}
                        onChange={(item = []) => {
                            setFormData({ ...formData, tagIds: item?.map(x => x.id) || [] })
                        }}
                        options={tags}
                        textField="label"
                        valueField="id"
                        loading={loadingTags}
                        isClearable
                        placeholder={messages['select']}
                        multiple
                        menuPlacement='top'
                    />
                </Grid>
            </Form>
            <div className="create-edit-buttons" style={{ width: '100%', backgroundColor: '#fff' }}>
                <Button
                    disabled={loadingCreateEdit}
                    className="btn-cancel"
                    onClick={(e) => {
                        if (e) e.preventDefault();
                        dispatch(sizeDashboardPanelCreateEditModal('small'));
                        handleClose();
                    }}
                >
                    {messages['cancel']}
                </Button>
                <Button
                    disabled={loadingCreateEdit}
                    onClick={(e) => devicesByStatusForm.current.submit()}
                    className="btn-dark-blue"
                >
                    {messages['save']}
                </Button>
            </div>
        </div>
    )
}

export default CreateEditDeviceByStatus